import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { GetPatientDetails } from '../config/PatientApi';
import { Card, Nav, Tab } from 'react-bootstrap';
import useAdvanceTable from 'hooks/useAdvanceTable';
import {
    faHistory,
    faShieldAlt,
    faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';

// Sub-components
import PatientViewHeader from './patient-view/PatientViewHeader';
import PatientDetailsTab from './patient-view/PatientDetailsTab';
import PatientHistoryTab from './patient-view/PatientHistoryTab';
import StatusBadge from './patient-view/StatusBadge';

const PatientView = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const { patientDetails } = useSelector((state) => state.patient);
    const { allPermissionType } = useSelector((state) => state.role);
    const patientSetting = allPermissionType?.[0]?.rolemngmt_name?.find(
        (item) => item?.rolemngmttyp_name === "Patient"
    );

    useEffect(() => {
        if (id) {
            dispatch(GetPatientDetails(id));
        }
    }, [id, dispatch]);

    const policyColumns = [
        {
            accessorKey: 'index',
            header: '#',
            cell: ({ row }) => (
                <div className="d-flex align-items-center">
                    <div
                        className="rounded-circle bg-200 d-flex flex-center fs-11"
                        style={{ width: "30px", height: "30px" }}
                    >
                        {row.index + 1}
                    </div>
                </div>
            )
        },
        {
            accessorKey: 'created_at',
            header: 'Date',
            cell: ({ row: { original } }) => {
                return original.created_at ? moment(original.created_at).format('DD-MM-YYYY hh:mm:ss A') : '';
            }
        },
        {
            accessorKey: 'patpolcy_name',
            header: 'Policy Name',
        },
        {
            accessorKey: 'patpolcy_number',
            header: 'Policy Number',
        },
        {
            accessorKey: 'patpolcy_status',
            header: 'Status',
            cell: ({ row: { original } }) => (
                <StatusBadge status={original.patpolcy_status} />
            )
        },

    ];

    const insuranceColumns = [
        {
            accessorKey: 'index',
            header: '#',
            cell: ({ row }) => (
                <div className="d-flex align-items-center">
                    <div
                        className="rounded-circle bg-200 d-flex flex-center fs-11"
                        style={{ width: "30px", height: "30px" }}
                    >
                        {row.index + 1}
                    </div>
                </div>
            )
        },
        {
            accessorKey: 'created_at',
            header: 'Date',
            cell: ({ row: { original } }) => {
                return original.created_at ? moment(original.created_at).format('DD-MM-YYYY hh:mm:ss A') : '';
            }
        },
        {
            accessorKey: 'patient_ins_clsnme',
            header: 'Class Name',
        },
        {
            accessorKey: 'patient_ins_network',
            header: 'Network',
        },
        {
            accessorKey: 'patient_ins_status',
            header: 'Status',
            cell: ({ row: { original } }) => (
                <StatusBadge status={original.patient_ins_status} />
            )
        },

    ];

    const policyTable = useAdvanceTable({
        data: patientDetails?.patientpolicy_history || [],
        columns: policyColumns,
        pagination: true,
        perPage: 10,
    });

    const insuranceTable = useAdvanceTable({
        data: patientDetails?.patientinsurance_history || [],
        columns: insuranceColumns,
        pagination: true,
        perPage: 10,
    });

    if (!patientDetails) {
        return;
    }

    return (
        Number(patientSetting?.view) === 1 ? (
            <div className="patient-view-container pb-4">
                <Card className="mb-3 border-0 shadow-sm overflow-hidden">
                    <PatientViewHeader patientDetails={patientDetails} />
                    <Card.Body className="p-0">
                        <Tab.Container id="patient-tabs" defaultActiveKey="details">
                            <div className=" border-bottom px-3">
                                <Nav variant="tabs" className="border-0">
                                    <Nav.Item>
                                        <Nav.Link eventKey="details" className="fs-10 py-3 px-4 fw-bold">
                                            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                                            Patient Details
                                        </Nav.Link>
                                    </Nav.Item>
                                    <Nav.Item>
                                        <Nav.Link eventKey="policy" className="fs-10 py-3 px-4 fw-bold">
                                            <FontAwesomeIcon icon={faHistory} className="me-2" />
                                            Policy History
                                        </Nav.Link>
                                    </Nav.Item>
                                    <Nav.Item>
                                        <Nav.Link eventKey="insurance" className="fs-10 py-3 px-4 fw-bold">
                                            <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
                                            Insurance History
                                        </Nav.Link>
                                    </Nav.Item>
                                </Nav>
                            </div>

                            <div className="p-4">
                                <Tab.Content>
                                    <Tab.Pane eventKey="details">
                                        <PatientDetailsTab patientDetails={patientDetails} />
                                    </Tab.Pane>

                                    <Tab.Pane eventKey="policy">
                                        <PatientHistoryTab
                                            title="Policy History"
                                            table={policyTable}
                                            totalRecords={patientDetails?.patientpolicy_history?.length}
                                        />
                                    </Tab.Pane>

                                    <Tab.Pane eventKey="insurance">
                                        <PatientHistoryTab
                                            title="Insurance History"
                                            table={insuranceTable}
                                            totalRecords={patientDetails?.patientinsurance_history?.length}
                                        />
                                    </Tab.Pane>
                                </Tab.Content>
                            </div>
                        </Tab.Container>
                    </Card.Body>
                </Card>
            </div>
        ) : (
            <div className="py-6 text-center border rounded-3mb-3">
                <h5 className="text-danger">Permission Denied</h5>
                <p className="text-muted fs-11">
                    You do not have permission to view this Patient.
                </p>
            </div>
        )
    );
};

export default PatientView;
